<template>
  <div role="region" :aria-label="$t('topUp.title')">
    <vue-loading :active.sync="isLoading" :can-cancel="false" :is-full-page="true" />

    <div id="desktop-top-up" class="d-none d-md-block">
      <div class="top-up-desktop-layout">
        <div v-if="showLimitInfo" class="top-up-desktop-info-row">
          <StatusMessage id="top-up-limit-info-desktop" variant="warning">
            {{ limitInfoText }}
          </StatusMessage>
        </div>

        <div class="top-up-desktop-blocks-row">
        <BlockContainer :title="$t('customerCard.subTitle.info')" variant="narrow" v-if="isChild">
          <h3 class="normalText"><strong>{{ $t('customerCard.label.holder') }}:</strong> {{ fullName(wondCustomer) }}</h3>
          <h3 class="normalText"><strong>{{ $t('customerCard.label.number') }}:</strong> {{ wondCustomer.cardNum }}</h3>
          <h3 class="normalText"><strong>{{ $t('customerCard.label.balance') }}</strong> {{ wondCustomer.formattedPrepayBalanceCash }}</h3>
        </BlockContainer>

        <BlockContainer v-if="!Utils.isEmpty(config.CUSTOMER_CARD.topUp.predefinedValues)" id="predefinedTopUpValues" :title="$t('topUp.subTitle.chooseAmount')">
          <template #actions>
            <div class="predefinedButtonsWrapper" role="group" :aria-label="$t('topUp.subTitle.chooseAmount')">
              <div v-for="predefinedValue in config.CUSTOMER_CARD.topUp.predefinedValues" :key="`desktop-${predefinedValue}`">
                <Button
                  v-bind:key="`desktop-button-${predefinedValue}`"
                  @click="confirmTopUp(predefinedValue)"
                  :aria-label="$t('topUp.ariaLabel.chooseAmount',[predefinedValue + ' ' + config.ACCOUNT_BALANCE_CURRENCY])"
                  :disabled="!checkPreplayLimitHigh(predefinedValue)"
                >
                  {{ Utils.formatCurrency(predefinedValue, config.ACCOUNT_BALANCE_CURRENCY, true, config.ACCOUNT_BALANCE_SEPARATOR) }}
                </Button>
              </div>
            </div>
          </template>
        </BlockContainer>

        <BlockContainer v-if="config.CUSTOMER_CARD.topUp.customField.enabled" :title="$t('topUp.subTitle.customAmount')">
          <Slider id="customAmountDesktop" name="customAmount" :aria-label="$t('topUp.ariaLabel.customAmount')"
                  :aria-describedby="showLimitInfo ? 'top-up-limit-info-desktop' : null"
                  v-model="chargeAmount"
                  :currency="config.ACCOUNT_BALANCE_CURRENCY"
                  :currencyDisplay="'symbol'"
                  :min="config.CUSTOMER_CARD.topUp.customField.minValue"
                  :max="maxChargeAmount()"
                  :step="config.CUSTOMER_CARD.topUp.customField.step || 1"
                  :label="$t('topUp.label.customAmount')"
          />
          <template #actions>
            <Button @click.stop="confirmTopUp(chargeAmount)"
                    :aria-label="$t('topUp.ariaLabel.charge', [Utils.formatCurrency(chargeAmount, config.ACCOUNT_BALANCE_CURRENCY, true, config.ACCOUNT_BALANCE_SEPARATOR)])"
                    :disabled="chargeAmount > maxChargeAmount() || highestAllowedAmount() <= 0">
              {{ $t('topUp.button.charge', chargeAmount, config.ACCOUNT_BALANCE_CURRENCY) }}
            </Button>
          </template>
        </BlockContainer>
        </div>
      </div>
    </div>

    <div id="mobile-top-up" class="d-md-none">
      <div class="top-up-mobile-stack">

        <div v-if="isChild" class="topUpMobileInfo">
          <h3 class="normalText"><strong>{{ $t('customerCard.label.holder') }}:</strong> {{ fullName(wondCustomer) }}</h3>
          <h3 class="normalText"><strong>{{ $t('customerCard.label.number') }}:</strong> {{ wondCustomer.cardNum }}</h3>
          <h3 class="normalText"><strong>{{ $t('customerCard.label.balance') }}</strong> {{ wondCustomer.formattedPrepayBalanceCash }}</h3>
        </div>

        <StatusMessage v-if="showLimitInfo" id="top-up-limit-info-mobile" variant="warning">
          {{ limitInfoText }}
        </StatusMessage>

        <BlockContainer
          v-if="!Utils.isEmpty(config.CUSTOMER_CARD.topUp.predefinedValues)"
          variant="full"
          :label="$t('topUp.subTitle.chooseAmount')"
        >
          <h3 class="normalText">{{ $t('topUp.subTitle.chooseAmount') }}</h3>
          <div class="predefinedButtonsWrapper" role="group" :aria-label="$t('topUp.subTitle.chooseAmount')">
            <div v-for="predefinedValue in config.CUSTOMER_CARD.topUp.predefinedValues" :key="`mobile-${predefinedValue}`">
              <Button
                v-bind:key="`mobile-button-${predefinedValue}`"
                @click="confirmTopUp(predefinedValue)"
                :aria-label="$t('topUp.ariaLabel.chooseAmount',[predefinedValue + ' ' + config.ACCOUNT_BALANCE_CURRENCY])"
                :disabled="!checkPreplayLimitHigh(predefinedValue)"
              >
                {{ Utils.formatCurrency(predefinedValue, config.ACCOUNT_BALANCE_CURRENCY, true, config.ACCOUNT_BALANCE_SEPARATOR) }}
              </Button>
            </div>
          </div>
        </BlockContainer>

        <BlockContainer
          v-if="config.CUSTOMER_CARD.topUp.customField.enabled"
          variant="full"
          :label="$t('topUp.subTitle.customAmount')"
        >
          <h3 class="normalText">{{ $t('topUp.subTitle.customAmount') }}</h3>
          <Slider id="customAmountMobile" name="customAmountMobile" :aria-label="$t('topUp.ariaLabel.customAmount')"
                  :aria-describedby="showLimitInfo ? 'top-up-limit-info-mobile' : null"
                  v-model="chargeAmount"
                  :currency="config.ACCOUNT_BALANCE_CURRENCY"
                  :currencyDisplay="'symbol'"
                  :min="config.CUSTOMER_CARD.topUp.customField.minValue"
                  :max="maxChargeAmount()"
                  :step="config.CUSTOMER_CARD.topUp.customField.step || 1"
                  :label="$t('topUp.label.customAmount')"
          />
          <template #actions>
            <Button @click.stop="confirmTopUp(chargeAmount)"
                    :aria-label="$t('topUp.ariaLabel.charge', [Utils.formatCurrency(chargeAmount, config.ACCOUNT_BALANCE_CURRENCY, true, config.ACCOUNT_BALANCE_SEPARATOR)])"
                    :disabled="chargeAmount > maxChargeAmount() || highestAllowedAmount() <= 0">
              {{ $t('topUp.button.charge', chargeAmount, config.ACCOUNT_BALANCE_CURRENCY) }}
            </Button>
          </template>
        </BlockContainer>
      </div>
    </div>
  </div>
</template>

<script>
import Utils from "@/utils/utils";
import VueLoading from "vue-loading-overlay";
import {accountService} from "@/services/api/accountService";
import {useAppStore} from "@/stores/app";
import {paymentService} from "@/services/api/paymentService";
import {useOrderStore} from "@/stores/order";
import {Workflow} from "@/constants/constants";
import {useTopUpRefreshStore} from "@/stores/topUpRefresh";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import Button from "@/components/actions/Button.vue";
import Slider from "@/components/forms/Slider.vue";
import StatusMessage from "@/components/feedback/StatusMessage.vue";

export default {
  name: "top-up",
  components: {
    Button,
    BlockContainer,
    VueLoading,
    Slider,
    StatusMessage,
  },
  data() {
    return {
      isLoading: false,
      wondCustomer: {},
      chargeAmount: null,
    };
  },
  setup() {
    const appStore = useAppStore();
    const orderStore = useOrderStore();
    const topUpRefreshStore = useTopUpRefreshStore();

    return {
      appStore,
      orderStore,
      topUpRefreshStore
    };
  },
  computed: {
    Utils() {
      return Utils
    },
    config(){
      return window.config;
    },
    isChild() {
      return this.appStore.isWorkflow(Workflow.SCHOOL);
    },
    hasLimitData() {
      return this.wondCustomer?.prepayLimitHigh != null && this.wondCustomer?.prepayBalanceCash != null;
    },
    showLimitInfo() {
      return this.hasLimitData && this.checkLimitText();
    },
    limitInfoText() {
      if (!this.showLimitInfo) {
        return "";
      }

      const maxAllowedAmount = this.highestAllowedAmount();

      if (maxAllowedAmount <= 0) {
        return this.$t("topUp.messages.limit.reached");
      }

      const formattedMaxAllowed = Utils.formatCurrency(
        maxAllowedAmount,
        this.config.ACCOUNT_BALANCE_CURRENCY,
        true,
        this.config.ACCOUNT_BALANCE_SEPARATOR
      );

      return this.$t("topUp.messages.limit.maxAllowed", [formattedMaxAllowed]);
    },
  },
  props: {
    customerId: {
      type: [String, Number],
    }
  },
  methods: {
    fullName(customer) {
      const nameParts = [];

      if (customer.description) {
        nameParts.push(customer.description);
      }

      if (customer.firstName) {
        nameParts.push(customer.firstName);
      }

      return nameParts.join(' ').trim();
    },
    async selectChild(id) {
      this.isLoading = true;
      const result = await accountService.selectChild(id);
      this.isLoading = false;

      if (result.exception !== undefined || result.errorCode < 0) {
        this.makeToast('danger', this.$t('topUp.messages.error.onSelectChild'));
        return;
      }

      return result.wondChild;
    },
    async confirmTopUp(amount) {
      await this.configurePrepayment(amount);
    },
    async configurePrepayment(amount) {
      this.isLoading = true;
      try {
        const result = await paymentService.configurePrepayment(amount);

        if (!result || result.exception !== undefined || result.errorCode < 0) {
          this.makeToast('danger', this.$t('topUp.messages.error.onTopUp'));
          return;
        }

        if (this.isChild) {
          this.topUpRefreshStore.clear();
        } else {
          this.topUpRefreshStore.prepare(amount, this.wondCustomer);
        }

        this.$navigateToTopUpPayment();
      } catch (e) {
        this.topUpRefreshStore.clear();
        this.makeToast('danger', this.$t('topUp.messages.error.onTopUp'));
      } finally {
        this.isLoading = false;
      }
    },
    checkLimitText()
    {
      if (!Utils.isEmpty(this.config.CUSTOMER_CARD.topUp.predefinedValues))
      {
        const anyDisabled = this.config.CUSTOMER_CARD.topUp.predefinedValues.some(
          amount => !this.checkPreplayLimitHigh(amount)
        );
        if (anyDisabled)
        {
          return true;
        }
      }

      if (this.config.CUSTOMER_CARD.topUp.customField.enabled)
      {
        if (this.maxChargeAmount() < this.config.CUSTOMER_CARD.topUp.customField.maxValue)
        {
          return true;
        }
      }

      return false;
    },
    checkPreplayLimitHigh(amount)
    {
      const limit = this.wondCustomer?.prepayLimitHigh;
      const balance = this.wondCustomer?.prepayBalanceCash;
      const pendingTopups = this.wondCustomer?.pendingTopup || [];
      const pendingSum = pendingTopups.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
      if (limit == null || balance == null) {
        return true;
      }
      return balance + pendingSum + amount <= limit;
    },
    highestAllowedAmount() {
      const allowedAmounts = [];

      if (!Utils.isEmpty(this.config.CUSTOMER_CARD.topUp.predefinedValues)) {
        const allowedPredefinedValues = this.config.CUSTOMER_CARD.topUp.predefinedValues.filter(
          amount => this.checkPreplayLimitHigh(amount)
        );
        if (allowedPredefinedValues.length > 0) {
          allowedAmounts.push(Math.max(...allowedPredefinedValues));
        }
      }

      if (this.config.CUSTOMER_CARD.topUp.customField.enabled) {
        allowedAmounts.push(this.maxChargeAmount());
      }

      if (allowedAmounts.length === 0) {
        return 0;
      }

      return Math.max(...allowedAmounts);
    },
    maxChargeAmount() {
      const limit = this.wondCustomer?.prepayLimitHigh;
      const balance = this.wondCustomer?.prepayBalanceCash;
      const pendingTopups = this.wondCustomer?.pendingTopup || [];
      const pendingSum = pendingTopups.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0);
      if (limit == null || balance == null) {
        return this.config.CUSTOMER_CARD.topUp.customField.maxValue;
      }
      const reducedMax = Math.min(this.config.CUSTOMER_CARD.topUp.customField.maxValue, limit - balance - pendingSum);
      return Math.max(0, Math.floor(reducedMax * 100) / 100);
    },
  },
  async created() {
    this.chargeAmount = this.config.CUSTOMER_CARD.topUp.customField.minValue;

    if (this.isChild) {
      this.wondCustomer = await this.selectChild(this.customerId);
    }
    else
    {
      const result = await accountService.afterLogin();
      if (result.wondCustomer) {
        this.wondCustomer = result.wondCustomer;
      }
    }
  },
};
</script>
<style scoped>

.top-up-mobile-stack {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.top-up-desktop-layout {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.top-up-desktop-info-row {
  width: 100%;
}

.top-up-desktop-blocks-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  align-items: stretch;
}

.top-up-desktop-blocks-row > * {
  flex: 1 1 320px;
  min-width: 280px;
}

.topUpMobileInfo {
  margin-bottom: 1rem;
}

.predefinedButtonsWrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
}


/* WCAG-friendly touch targets for amount and confirm actions. */
.predefinedButtonsWrapper :deep(button),
.top-up-mobile-stack :deep(.actions button),
.top-up-desktop-blocks-row :deep(.actions button) {
  min-height: 44px;
}

@media (min-width: 768px) and (max-width: 1199.98px) {
  .top-up-desktop-blocks-row > * {
    flex: 1 1 calc(50% - 0.75rem);
    min-width: 0;
  }

  .predefinedButtonsWrapper {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/*@media (max-width: 767.98px) {
  .predefinedButtonsWrapper {
    grid-template-columns: 1fr;
  }
}*/
</style>
